import { useState, useCallback, useEffect } from 'react';
import { toast } from 'react-toastify';
import { SOLICITUD_ALTERNATIVAS_API } from '../../../shared/services/solicitudAlternativas';
import ActionsPanel from '../ActionsPanel';
import ActionFormPanel from '../ActionFormPanel';
import ActionButton from '../ActionButton';
import ActionCommentField from '../ActionCommentField';
import NotasInternasList from '../NotasInternasList';
import ComentariosHistorialList from '../ComentariosHistorialList';
import LoadingInline from '../layout/LoadingInline';
import { ModalSection, ModalPanel } from '../layout/ModalSection';
import PresupuestoOrigenFields from '../PresupuestoOrigenFields';
import AlternativaDetalleAlternativasGrid from '../AlternativaDetalleAlternativasGrid';
import SinAlternativaDetallePanel from '../SinAlternativaDetallePanel';
import { ACCIONES_AT_CLIENTE, getActionById, API_ACCION_BY_UI_ID } from '../actionConfig';
import { hasAtClienteAssigned } from '../../actions';
import {
    ESTADO_FINALIZADA,
    ESTADO_MAS_INFORMACION,
    getEstadoId,
    normalizePiezas,
} from '../../utils';

const SUCCESS_MESSAGE_BY_ACTION = {
    accept: 'Alternativa aceptada. Derivada a finanzas.',
    reject: 'Alternativa rechazada. Derivada a finanzas.',
    requestInfo: 'Solicitud de información enviada al recambista.',
};

const CLOSING_ACTIONS = new Set(['accept', 'reject', 'requestInfo']);

const DetalleRevisionView = ({ currentItem, onClose, onSolicitudUpdated }) => {
    const [activeAction, setActiveAction] = useState(null);
    const [comment, setComment] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [detalleData, setDetalleData] = useState(null);
    const [loading, setLoading] = useState(true);

    const reloadDetalle = useCallback(async () => {
        const id = currentItem?.id;
        if (id == null) {
            setDetalleData(null);
            setLoading(false);
            return;
        }
        setLoading(true);
        try {
            const res = await SOLICITUD_ALTERNATIVAS_API.getDetalleRevision(id);
            if (res?.success) {
                setDetalleData(res.data ?? null);
            } else {
                toast.error(res?.message || 'No se han podido cargar los datos.');
                setDetalleData(null);
            }
        } catch (e) {
            toast.error(
                e?.response?.data?.message ||
                    e?.message ||
                    'No se han podido cargar los datos.',
            );
            setDetalleData(null);
        } finally {
            setLoading(false);
        }
    }, [currentItem?.id]);

    useEffect(() => {
        reloadDetalle();
    }, [reloadDetalle]);

    const origen = currentItem?.origen;
    const piezasOrigen =
        detalleData?.piezas_origen?.length > 0
            ? detalleData.piezas_origen
            : normalizePiezas(currentItem);
    const alternativa = detalleData?.alternativa;
    const sinAlternativa = detalleData?.sin_alternativa;
    const piezasAlternativa = alternativa?.piezas ?? [];
    const notasInternas = detalleData?.notas_internas || [];
    const comentariosParaMostrar = detalleData?.comentarios_historial || [];
    const activeConfig = getActionById(activeAction);
    const estadoId = getEstadoId(currentItem);
    const isMasInformacion = estadoId === ESTADO_MAS_INFORMACION;
    const isFinalizada = estadoId === ESTADO_FINALIZADA;
    const canRunAtActions =
        hasAtClienteAssigned(currentItem?.crafted_by) &&
        !isMasInformacion &&
        !isFinalizada;

    const handleCopyAlternativaUrl = useCallback((url) => {
        if (!url) return;
        navigator.clipboard.writeText(url);
        toast.success('Link copiado al portapapeles.');
    }, []);

    const toggleAction = (action) => {
        setActiveAction((prev) => (prev === action ? null : action));
        setComment('');
    };

    const handleSubmit = useCallback(async () => {
        const config = getActionById(activeAction);
        if (!config) return;

        const trimmed = comment.trim();

        if (config.required && !trimmed) {
            toast.error(config.requiredMessage);
            return;
        }

        setSubmitting(true);
        try {
            const accion = API_ACCION_BY_UI_ID[activeAction];
            const payload = {
                solicitud_id: currentItem.id,
                accion,
            };
            if (trimmed !== '') {
                payload.comentario = trimmed;
            }

            const res =
                await SOLICITUD_ALTERNATIVAS_API.ejecutarAccionAtCliente(
                    payload,
                );

            if (!res?.success) {
                toast.error(res?.message || 'Ha ocurrido un error.');
                return;
            }

            const message = SUCCESS_MESSAGE_BY_ACTION[activeAction];
            if (message) {
                toast.success(message);
            }

            if (CLOSING_ACTIONS.has(activeAction)) {
                onSolicitudUpdated?.(res.data);
                onClose();
            }
        } catch (e) {
            toast.error(
                e?.response?.data?.message ||
                    e?.message ||
                    'Ha ocurrido un error.',
            );
        } finally {
            setSubmitting(false);
        }
    }, [
        activeAction,
        comment,
        currentItem?.id,
        onClose,
        onSolicitudUpdated,
    ]);

    if (loading) {
        return <LoadingInline />;
    }

    return (
        <div>
            <ModalSection title="Presupuesto origen">
                <ModalPanel>
                    <div className="row g-3">
                        <PresupuestoOrigenFields
                            layout="atRevision"
                            origen={origen}
                            piezas={piezasOrigen}
                            precioVentaOrigen={detalleData?.precio_venta_origen}
                            clienteDisplay="nameOnly"
                        />
                    </div>
                </ModalPanel>
            </ModalSection>

            <ModalSection
                title={
                    sinAlternativa
                        ? 'Sin alternativa'
                        : 'Respuesta del recambista'
                }
            >
                <ModalPanel>
                    {sinAlternativa ? (
                        <SinAlternativaDetallePanel
                            sinAlternativa={sinAlternativa}
                        />
                    ) : (
                        <AlternativaDetalleAlternativasGrid
                            variant="full"
                            alternativa={alternativa}
                            precioOk={currentItem.precio_ok}
                            onCopyPiezaUrl={
                                piezasAlternativa.some((p) => p.url)
                                    ? handleCopyAlternativaUrl
                                    : undefined
                            }
                        />
                    )}
                </ModalPanel>
            </ModalSection>

            <ModalSection title="Comentarios">
                <ModalPanel>
                    <ComentariosHistorialList
                        comentarios={comentariosParaMostrar}
                    />
                </ModalPanel>
            </ModalSection>

            <ModalSection title="Acciones atención al cliente">
                <ModalPanel>
                    {isFinalizada ? (
                        <span
                            style={{
                                display: 'block',
                                color: '#6c757d',
                                fontSize: '0.85rem',
                                lineHeight: 1.5,
                            }}
                        >
                            La solicitud está finalizada. No se pueden realizar
                            más acciones de atención al cliente.
                        </span>
                    ) : isMasInformacion ? (
                        <span
                            style={{
                                display: 'block',
                                color: '#856404',
                                fontSize: '0.85rem',
                                lineHeight: 1.5,
                            }}
                        >
                            Pendiente de que el recambista aporte la información
                            solicitada. Usa la acción «Aportar información» desde
                            el menú de la tabla.
                        </span>
                    ) : !canRunAtActions ? (
                        <span
                            style={{
                                display: 'block',
                                color: '#856404',
                                fontSize: '0.85rem',
                                lineHeight: 1.5,
                            }}
                        >
                            Asigna un responsable de atención al cliente desde el
                            menú de la tabla para poder gestionar esta
                            solicitud.
                        </span>
                    ) : (
                        <>
                            <ActionsPanel
                                actions={ACCIONES_AT_CLIENTE}
                                activeAction={activeAction}
                                onSelectAction={toggleAction}
                            />

                            {activeConfig ? (
                                <ActionFormPanel
                                    color={activeConfig.color}
                                    title={activeConfig.title}
                                    description={
                                        activeConfig.description
                                    }
                                >
                                    <ActionCommentField
                                        label={activeConfig.fieldLabel}
                                        placeholder={
                                            activeConfig.placeholder
                                        }
                                        value={comment}
                                        onChange={(e) =>
                                            setComment(e.target.value)
                                        }
                                        required={
                                            activeConfig.required
                                        }
                                    />
                                    <div className="d-flex justify-content-end">
                                        <ActionButton
                                            color={activeConfig.color}
                                            disabled={
                                                submitting ||
                                                (activeConfig.required &&
                                                    !comment.trim())
                                            }
                                            loading={submitting}
                                            label={
                                                activeConfig.buttonLabel
                                            }
                                            onClick={handleSubmit}
                                        />
                                    </div>
                                </ActionFormPanel>
                            ) : (
                                <span
                                    style={{
                                        color: '#9e9e9e',
                                        fontSize: '0.85rem',
                                    }}
                                >
                                    Selecciona una acción para continuar
                                </span>
                            )}
                        </>
                    )}
                </ModalPanel>
            </ModalSection>

            <ModalSection title="Notas internas">
                <ModalPanel>
                    <NotasInternasList notas={notasInternas} />
                </ModalPanel>
            </ModalSection>
        </div>
    );
};

export default DetalleRevisionView;
